/*
 * File: InfiniteScroll.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on February 4, 2022 at 4:30 PM
 * Copyright © 2022 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: June 4, 2025 at 6:35 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, {
	memo, useEffect, useMemo, useState
} from 'react';
import PropTypes from 'prop-types';
import InfiniteScrollComponent from 'react-infinite-scroll-component';
import PullToRefresh from 'react-simple-pull-to-refresh';
import Lottie from 'react-lottie-player';
import { replaceColor } from 'lottie-colorify';
import { useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';

// Hooks
import useMediaQuery from '../../hooks/useMediaQuery';

// Animation
import animationData from '../../assets/animations/spinner.json';

// Constants
import { mobileBreakpoint } from '../../styles/constants';
import { AVAILABLE_I18N_NAMESPACES, UI_MODE_OPTIONS } from '../../../constants/base.constants';

// Components
import { Typography } from '../Typography';

// Styles
import * as S from './InfiniteScroll.styles';
import { DarkTheme, LightTheme } from '../../styles/colors';


/**
 * Component
 */

export const InfiniteScroll = memo(({
	children, dataLength, next, refresh, loader, hasMore, pullToRefresh
}) => {

	// Create state handlers
	const [showPullToRefresh, setShowPullToRefresh] = useState(false);

	// Check if mobile screen size
	const isMobile = useMediaQuery(`(max-width: ${mobileBreakpoint}em)`);

	// Get partner if necessary
	const partner = useSelector((state) => state.partner.value);

	// Get current UI mode from hook
	const uiMode = useSelector((state) => state.ui.value);

	// Define current theme
	const currentTheme = uiMode.mode === UI_MODE_OPTIONS.DARK ? DarkTheme(partner) : LightTheme(partner);

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Handle actions on component load
	useEffect(() => {

		// Show pull to refresh
		setShowPullToRefresh(true);

	}, []);

	// Render infinite scroll
	const RenderedInfiniteScroll = useMemo(() => (
		<InfiniteScrollComponent
			dataLength={dataLength} // This is important field to render the next data
			next={next}
			hasMore={hasMore}
			style={{ padding: '1px', overflow: 'visible' }}
			loader={loader}
			endMessage={(
				<S.EndContainer>
					<Typography tag="p" weight="medium" variation="3" center>
						<Trans t={t} i18nKey="infiniteScroll.endMessage">
							That&apos;s everything for now.
						</Trans>
					</Typography>
				</S.EndContainer>
			)}
			refreshFunction={refresh}
		>
			{children}
		</InfiniteScrollComponent>
	), [children, dataLength, hasMore, loader, next, refresh, t]);

	// Render component
	return pullToRefresh ? (
		<PullToRefresh
			isPullable={isMobile}
			pullDownThreshold={80}
			maxPullDownDistance={100}
			onRefresh={refresh}
			refreshingContent={showPullToRefresh ? (
				<S.PullLoadingIndicator>
					<Lottie loop play animationData={replaceColor('#ffffff', currentTheme.brandPrimaryBase, animationData)} style={{ height: '100%' }} />
				</S.PullLoadingIndicator>
			) : null}
			pullingContent={showPullToRefresh ? (
				<S.PullPrompt>
					<Typography tag="p" variation="3" center>
						<Trans t={t} i18nKey="infiniteScroll.pullInstructions">
							Pull to refresh
						</Trans>
					</Typography>
				</S.PullPrompt>
			) : null}
		>
			{RenderedInfiniteScroll}
		</PullToRefresh>
	) : RenderedInfiniteScroll;
});


/**
 * Configuration
 */

InfiniteScroll.displayName = 'InfiniteScroll';
InfiniteScroll.propTypes = {
	children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
	dataLength: PropTypes.number,
	next: PropTypes.func,
	refresh: PropTypes.func,
	hasMore: PropTypes.bool,
	loader: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
	pullToRefresh: PropTypes.bool
};
InfiniteScroll.defaultProps = {
	children: null,
	dataLength: 0,
	next: null,
	refresh: null,
	hasMore: true,
	loader: null,
	pullToRefresh: true
};
